import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Grid,
  Collapse
} from '@mui/material';
import { 
  Add, 
  Edit, 
  Delete, 
  AttachMoney, 
  Payment,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { dettesAPI, clientsAPI, paiementsAPI } from '../../services/api';

const DettesPage = () => {
  const [dettes, setDettes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPaiementDialog, setOpenPaiementDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentDette, setCurrentDette] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [formData, setFormData] = useState({
    montant_dette: '',
    statut: 'en cours',
    id_client: ''
  });
  const [paiementData, setPaiementData] = useState({
    montant: '',
    mode_paiement: '',
    date_paiement: new Date().toISOString().split('T')[0]
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dettesResponse, clientsResponse] = await Promise.all([
        dettesAPI.getAll(),
        clientsAPI.getAll()
      ]);
      setDettes(dettesResponse.data.data || []);
      setClients(clientsResponse.data.data || []);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (dette = null) => {
    if (dette) {
      setEditMode(true);
      setCurrentDette(dette);
      setFormData({
        montant_dette: dette.montant_dette || '',
        statut: dette.statut || 'en cours',
        id_client: dette.id_client || ''
      });
    } else {
      setEditMode(false);
      setCurrentDette(null);
      setFormData({
        montant_dette: '',
        statut: 'en cours',
        id_client: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentDette(null);
  };

  const handleOpenPaiementDialog = (dette) => {
    setCurrentDette(dette);
    setPaiementData({
      montant: '',
      mode_paiement: '',
      date_paiement: new Date().toISOString().split('T')[0]
    });
    setOpenPaiementDialog(true);
  };

  const handleClosePaiementDialog = () => {
    setOpenPaiementDialog(false);
    setCurrentDette(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePaiementChange = (e) => {
    setPaiementData({
      ...paiementData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        montant_dette: parseFloat(formData.montant_dette) || 0
      };

      if (editMode && currentDette) {
        await dettesAPI.update(currentDette.id_dette, dataToSend);
        showSnackbar('Dette modifiée avec succès', 'success');
      } else {
        await dettesAPI.create(dataToSend);
        showSnackbar('Dette créée avec succès', 'success');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

const handlePaiementSubmit = async (e) => {
  e.preventDefault();
  try {
    const dataToSend = {
      montant: parseFloat(paiementData.montant),
      mode_paiement: paiementData.mode_paiement,
      date_paiement: paiementData.date_paiement,
      id_dette: currentDette.id_dette
    };

    await paiementsAPI.create(dataToSend);
    showSnackbar('Paiement enregistré avec succès', 'success');
    handleClosePaiementDialog();
    loadData(); // Recharge les données pour mettre à jour l'affichage
  } catch (error) {
    console.error('Erreur paiement:', error);
    showSnackbar('Erreur lors de l\'enregistrement du paiement', 'error');
  }
};

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette dette ?')) {
      try {
        await dettesAPI.delete(id);
        showSnackbar('Dette supprimée avec succès', 'success');
        loadData();
      } catch (error) {
        showSnackbar('Erreur lors de la suppression', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getClientName = (idClient) => {
    const client = clients.find(c => c.id_client === idClient);
    return client ? `${client.nom} ${client.prenom}` : 'N/A';
  };

  const handleRowExpand = (detteId) => {
    setExpandedRow(expandedRow === detteId ? null : detteId);
  };

  const calculateTotalPaye = (paiements) => {
    if (!paiements || paiements.length === 0) return 0;
    return paiements.reduce((sum, p) => sum + (parseFloat(p.montant) || 0), 0);
  };

  const calculateReste = (dette) => {
    const total = parseFloat(dette.montant_dette) || 0;
    const paye = calculateTotalPaye(dette.Paiements);
    return total - paye;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  const dettesEnCours = dettes.filter(d => d.statut === 'en cours');
  const dettesReglees = dettes.filter(d => d.statut === 'réglée');
  const totalDettes = dettesEnCours.reduce((sum, d) => sum + calculateReste(d), 0);

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <AttachMoney sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestion des Dettes & Paiements
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={clients.length === 0}
        >
          Nouvelle Dette
        </Button>
      </Box>

      {clients.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Vous devez d'abord créer des clients avant d'ajouter des dettes.
        </Alert>
      )}

      {/* Statistiques */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Dettes en cours
              </Typography>
              <Typography variant="h4" color="error">
                {totalDettes.toFixed(2)} €
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Nombre de dettes actives
              </Typography>
              <Typography variant="h4" color="warning.main">
                {dettesEnCours.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Dettes réglées
              </Typography>
              <Typography variant="h4" color="success.main">
                {dettesReglees.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell width={50}></TableCell>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Client</strong></TableCell>
              <TableCell><strong>Montant Total</strong></TableCell>
              <TableCell><strong>Payé</strong></TableCell>
              <TableCell><strong>Reste</strong></TableCell>
              <TableCell><strong>Statut</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dettes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary">
                    Aucune dette trouvée. Cliquez sur "Nouvelle Dette" pour en ajouter une.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              dettes.map((dette) => {
                const paye = calculateTotalPaye(dette.Paiements);
                const reste = calculateReste(dette);
                const isExpanded = expandedRow === dette.id_dette;

                return (
                  <React.Fragment key={dette.id_dette}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleRowExpand(dette.id_dette)}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{dette.id_dette}</TableCell>
                      <TableCell>
                        <strong>{getClientName(dette.id_client)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{dette.montant_dette} €</strong>
                      </TableCell>
                      <TableCell>
                        <Chip label={`${paye.toFixed(2)} €`} color="success" size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${reste.toFixed(2)} €`} 
                          color={reste > 0 ? 'error' : 'success'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        {dette.statut === 'réglée' ? (
                          <Chip 
                            icon={<CheckCircle />} 
                            label="Réglée" 
                            color="success" 
                            size="small" 
                          />
                        ) : (
                          <Chip 
                            icon={<Cancel />} 
                            label="En cours" 
                            color="warning" 
                            size="small" 
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          color="success" 
                          size="small"
                          onClick={() => handleOpenPaiementDialog(dette)}
                          disabled={dette.statut === 'réglée'}
                          title="Ajouter un paiement"
                        >
                          <Payment />
                        </IconButton>
                        <IconButton 
                          color="primary" 
                          size="small"
                          onClick={() => handleOpenDialog(dette)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={() => handleDelete(dette.id_dette)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom>
                              Historique des paiements
                            </Typography>
                            {!dette.Paiements || dette.Paiements.length === 0 ? (
                              <Typography color="text.secondary">
                                Aucun paiement enregistré
                              </Typography>
                            ) : (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Montant</TableCell>
                                    <TableCell>Mode de paiement</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {dette.Paiements.map((paiement, index) => (
                                    <TableRow key={index}>
                                      <TableCell>
                                        {new Date(paiement.date_paiement).toLocaleDateString('fr-FR')}
                                      </TableCell>
                                      <TableCell>{paiement.montant} €</TableCell>
                                      <TableCell>{paiement.mode_paiement}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog d'ajout/modification dette */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Modifier la dette' : 'Nouvelle dette'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              name="id_client"
              label="Client"
              fullWidth
              margin="normal"
              value={formData.id_client}
              onChange={handleChange}
              required
              select
            >
              {clients.map((client) => (
                <MenuItem key={client.id_client} value={client.id_client}>
                  {client.nom} {client.prenom}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              name="montant_dette"
              label="Montant de la dette (€)"
              type="number"
              fullWidth
              margin="normal"
              value={formData.montant_dette}
              onChange={handleChange}
              required
              inputProps={{ step: "0.01", min: "0" }}
            />
            <TextField
              name="statut"
              label="Statut"
              fullWidth
              margin="normal"
              value={formData.statut}
              onChange={handleChange}
              required
              select
            >
              <MenuItem value="en cours">En cours</MenuItem>
              <MenuItem value="réglée">Réglée</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained">
              {editMode ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog d'ajout de paiement */}
      <Dialog open={openPaiementDialog} onClose={handleClosePaiementDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Ajouter un paiement
        </DialogTitle>
        <form onSubmit={handlePaiementSubmit}>
          <DialogContent>
            {currentDette && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Dette de {currentDette.montant_dette} € - Reste à payer : {calculateReste(currentDette).toFixed(2)} €
              </Alert>
            )}
            <TextField
              name="montant"
              label="Montant du paiement (€)"
              type="number"
              fullWidth
              margin="normal"
              value={paiementData.montant}
              onChange={handlePaiementChange}
              required
              inputProps={{ step: "0.01", min: "0" }}
            />
            <TextField
              name="mode_paiement"
              label="Mode de paiement"
              fullWidth
              margin="normal"
              value={paiementData.mode_paiement}
              onChange={handlePaiementChange}
              required
              select
            >
              <MenuItem value="Espèces">Espèces</MenuItem>
              <MenuItem value="Carte bancaire">Carte bancaire</MenuItem>
              <MenuItem value="Chèque">Chèque</MenuItem>
              <MenuItem value="Virement">Virement</MenuItem>
            </TextField>
            <TextField
              name="date_paiement"
              label="Date du paiement"
              type="date"
              fullWidth
              margin="normal"
              value={paiementData.date_paiement}
              onChange={handlePaiementChange}
              required
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePaiementDialog}>Annuler</Button>
            <Button type="submit" variant="contained" color="success">
              Enregistrer le paiement
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DettesPage;